import React, { FC, memo, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGetCurrencySymbol } from 'app/appState';
import { Box } from 'entities/Box';
import { ContractInfo } from 'entities/Contract';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { formatPrice } from 'shared/utils/helpers/formatPrice';
import { formatVatRate, getVatRateRow } from 'shared/utils/helpers/formatVatRate';

interface ModernPriceDetailsCardProps {
  contractInfo: ContractInfo;
}

export const ModernPriceDetailsCard: FC<ModernPriceDetailsCardProps> = memo((props) => {
  const { contractInfo } = props;

  const { t } = useAppTranslation('booking');

  const currencySymbol = useGetCurrencySymbol();

  const priceDetailsBullets = useMemo(() => {
    const tableRows: TableRow[] = [];

    const firstServicePeriodUnitPrice = contractInfo.contractPeriodsUnitPrices[0];
    // const secondServicePeriodUnitPrice = contractInfo.contractPeriodsUnitPrices[1];

    if (firstServicePeriodUnitPrice) {
      tableRows.push({
        label: t('Rental price'), // t('1st {{invoiceFrequencyType}} rent', { invoiceFrequencyType: t(contractInfo.invoiceFrequencyType) }),
        value: `${formatPrice(roundNumber(firstServicePeriodUnitPrice.initialRate))} ${currencySymbol}`,
      });
    }

    // if (secondServicePeriodUnitPrice && contractInfo.payForEntirePeriod) {
    //   tableRows.push({
    //     label: t('2nd {{invoiceFrequencyType}} rent', { invoiceFrequencyType: t(contractInfo.invoiceFrequencyType) }),
    //     value: `${roundNumber(secondServicePeriodUnitPrice.rate)} ${currencySymbol}`,
    //   });
    // }
    tableRows.push({
      label: t('Contract duration'),
      value: `${contractInfo.contractDuration} ${
        contractInfo.contractDuration > 1
          ? t('month', {
              count: contractInfo.contractDuration,
            })
          : t(contractInfo.invoiceFrequencyType)
      }`,
    });

    if (contractInfo.discountAmount && contractInfo.discountPercent) {
      tableRows.push({
        label: t('Discount'),
        value: `${formatPrice(roundNumber(contractInfo.discountAmount * contractInfo.contractDuration))} ${currencySymbol}`,
      });
    }

    if (contractInfo.vatRatePercent !== contractInfo.insuranceVatRatePercent) {
      tableRows.push(
        getVatRateRow(
          t,
          contractInfo.vatRatePercent,
          `${formatPrice(roundNumber(contractInfo.vatRateAmount + contractInfo.insuranceVatRateAmount))} ${currencySymbol}`,
        ),
      );
    }

    if (contractInfo.insuranceId) {
      if (contractInfo.payForEntirePeriod) {
        tableRows.push({
          label: t('Insurance for {{contractDuration}} {{invoiceFrequencyType}}', {
            contractDuration: contractInfo.contractDuration,
            invoiceFrequencyType: t(`${contractInfo.invoiceFrequencyType}s`),
          }),
          value: `${formatPrice(roundNumber(contractInfo.insuranceAmountWithoutVat))} ${currencySymbol}`,
        });
      } else {
        tableRows.push({
          label: t('Insurance for 1st {{invoiceFrequencyType}}', {
            invoiceFrequencyType: t(contractInfo.invoiceFrequencyType),
          }),
          value: `${formatPrice(roundNumber(contractInfo.insuranceAmountWithoutVat))} ${currencySymbol}`,
        });
      }

      if (contractInfo.vatRatePercent !== contractInfo.insuranceVatRatePercent) {
        tableRows.push(
          getVatRateRow(
            t,
            contractInfo.insuranceVatRatePercent,
            `${formatPrice(roundNumber(contractInfo.insuranceVatRateAmount))} ${currencySymbol}`,
            'Insurance VAT ({{vatRatePercent}}%)',
            'Insurance VAT',
          ),
        );
      }
    }

    /* tableRows.push({
      label: t('Deposit (one time)'),
      value: contractInfo.depositAmount ? `${contractInfo.depositAmount} ${currencySymbol}` : t('No deposit'),
    }); */

    const totalWithoutVat = roundNumber(contractInfo.totalToPay - contractInfo.vatRateAmount - contractInfo.insuranceVatRateAmount);
    const totalWithVat = contractInfo.totalToPay;

    if (totalWithoutVat === totalWithVat) {
      tableRows.push({
        label: t('VAT'),
        value: formatVatRate(0, t),
      });

      if (contractInfo.balanceToChargeAmount) {
        tableRows.push({
          label: t(
            `Total for ${
              contractInfo.payForEntirePeriod ? '{{contractDuration}} {{invoiceFrequencyTypes}}' : '1st {{invoiceFrequencyType}}'
            }`,
            {
              contractDuration: contractInfo.contractDuration,
              invoiceFrequencyType: t(contractInfo.invoiceFrequencyType),
              invoiceFrequencyTypes: t(`${contractInfo.invoiceFrequencyType}s`),
            },
          ),
          value: `${totalWithVat} ${currencySymbol}`,
        });

        tableRows.push({
          label: t('From user balance'),
          value: `- ${formatPrice(contractInfo.balanceToChargeAmount)} ${currencySymbol}`,
          color: 'text-accent',
          highlighted: true,
        });

        /* tableRows.push({
          label: t('Total to pay'),
          value: `${contractInfo.totalToCharge} ${currencySymbol}`,
          highlighted: true,
        }); */
      } /* else {
        tableRows.push({
          label: t(
            `Total to pay (for ${
              contractInfo.payForEntirePeriod ? '{{contractDuration}} {{invoiceFrequencyTypes}}' : '1st {{invoiceFrequencyType}}'
            })`,
            {
              contractDuration: contractInfo.contractDuration,
              invoiceFrequencyType: t(contractInfo.invoiceFrequencyType),
              invoiceFrequencyTypes: t(`${contractInfo.invoiceFrequencyType}s`),
            },
          ),
          value: `${totalWithVat} ${currencySymbol}`,
          highlighted: true,
        });
      } */
    } else {
      if (contractInfo.vatRatePercent === contractInfo.insuranceVatRatePercent) {
        tableRows.push(
          getVatRateRow(t, contractInfo.vatRatePercent, roundNumber(contractInfo.vatRateAmount + contractInfo.insuranceVatRateAmount)),
        );
      } else {
        const vatRow = getVatRateRow(
          t,
          contractInfo.vatRatePercent,
          `${formatPrice(roundNumber(contractInfo.vatRateAmount))} ${currencySymbol}`,
        );
        const isVatAlreadyDisplayed = tableRows.find((row) => row.label === vatRow.label);

        if (!isVatAlreadyDisplayed) {
          tableRows.push(vatRow);
        }
      }

      if (contractInfo.balanceToChargeAmount) {
        tableRows.push({
          label: t('Subtotal'),
          value: `${formatPrice(totalWithVat)} ${currencySymbol}`,
        });

        tableRows.push({
          label: t('From user balance'),
          value: `- ${formatPrice(contractInfo.balanceToChargeAmount)} ${currencySymbol}`,
          color: 'text-accent',
          highlighted: true,
        });

        /* tableRows.push({
          label: t('Total to pay'),
          value: `${contractInfo.totalToCharge} ${currencySymbol}`,
          highlighted: true,
        }); */
      } else {
        /* tableRows.push({
          label: t('Total to pay'),
          value: `${totalWithVat} ${currencySymbol}`,
          highlighted: true,
        }); */
      }
    }

    return tableRows;
  }, [
    contractInfo.contractPeriodsUnitPrices,
    contractInfo.payForEntirePeriod,
    contractInfo.totalToPay,
    contractInfo.vatRateAmount,
    contractInfo.insuranceVatRateAmount,
    contractInfo.invoiceFrequencyType,
    contractInfo.contractDuration,
    contractInfo.insuranceAmountWithoutVat,
    contractInfo.balanceToChargeAmount,
    contractInfo.totalToCharge,
    contractInfo.vatRatePercent,
    contractInfo.insuranceVatRatePercent,
    t,
    currencySymbol,
  ]);

  return (
    <div className="bg-white rounded-xl p-4">
      <div className="font-semibold text-xl mb-2 text-primary font-tektur">{t('Price details')}</div>

      <BulletsTable rows={priceDetailsBullets} theme="clear" />
    </div>
  );
});
